import React from 'react';
import { Mail, Phone } from 'lucide-react';
import SEO from '../components/SEO';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Privacy Policy | Data Protection | Equi Pulse Research"
        description="Read Equi Pulse Research privacy policy. Learn how we collect, use, and protect your personal information in compliance with data protection laws."
        url="/privacy-policy"
      />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-amber-400 mb-8">Privacy Policy</h1>
        
        <div className="prose prose-invert prose-amber max-w-none">
          <p className="text-gray-300 mb-6">
            www.equipulseresearch.com website is owned by <strong>Equi Pulse</strong>, which is a data controller of your personal data.
          </p>
          
          <p className="text-gray-300 mb-8">
            We have adopted this Privacy Policy, which determines how we are processing the information collected by www.equipulseresearch.com, which also provides the reasons why we must collect certain personal data about you. Therefore, you must read this Privacy Policy before using www.equipulseresearch.com website.
          </p>

          <p className="text-gray-300 mb-8">
            We take care of your personal data and undertake to guarantee its confidentiality and security.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Personal Information We Collect</h2>
          <p className="text-gray-300 mb-6">
            When you visit www.equipulseresearch.com, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the installed cookies on your device. Additionally, as you browse the Site, we collect information about the individual web pages or products you view, what websites or search terms referred you to the Site, and how you interact with the Site. We refer to this automatically-collected information as "Device Information." Moreover, we might collect the personal data you provide to us (including but not limited to Name, Surname, Address, payment information, etc.) during registration to be able to fulfill the agreement.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Why Do We Process Your Data?</h2>
          <p className="text-gray-300 mb-6">
            Our top priority is customer data security, and, as such, we may process only minimal user data, only as much as it is absolutely necessary to maintain the website. Information collected automatically is used only to identify potential cases of abuse and establish statistical information regarding website usage. This statistical information is not otherwise aggregated in such a way that it would identify any particular user of the system.
          </p>

          <p className="text-gray-300 mb-8">
            You can visit the website without telling us who you are or revealing any information, by which someone could identify you as a specific, identifiable individual. If, however, you wish to use some of the website's features, or you wish to receive our newsletter or provide other details by filling a form, you may provide personal data to us, such as your email, first name, last name, city of residence, organization, telephone number. You can choose not to provide us with your personal data, but then you may not be able to take advantage of some of the website's features. For example, you won't be able to receive our Newsletter or contact us directly from the website. Users who are uncertain about what information is mandatory are welcome to contact us via <a href="mailto:care@equipulseresearch.com" className="text-amber-400 hover:text-amber-300">care@equipulseresearch.com</a>.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Your Rights</h2>
          <p className="text-gray-300 mb-4">If you are a European resident, you have the following rights related to your personal data:</p>
          <ul className="list-disc list-inside text-gray-300 mb-8 space-y-2">
            <li>The right to be informed</li>
            <li>The right of access</li>
            <li>The right to rectification</li>
            <li>The right to erasure</li>
            <li>The right to restrict processing</li>
            <li>The right to data portability</li>
            <li>The right to object</li>
            <li>Rights in relation to automated decision-making and profiling</li>
          </ul>

          <p className="text-gray-300 mb-8">
            If you would like to exercise this right, please contact us through the contact information below.
          </p>

          <p className="text-gray-300 mb-8">
            Additionally, if you are a European resident, we note that we are processing your information in order to fulfill contracts we might have with you (for example, if you make an order through the Site), or otherwise to pursue our legitimate business interests listed above. Additionally, please note that your information might be transferred outside of Europe, including Canada and the United States.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Links to Other Websites</h2>
          <p className="text-gray-300 mb-8">
            Our website may contain links to other websites that are not owned or controlled by us. Please be aware that we are not responsible for such other websites or third parties' privacy practices. We encourage you to be aware when you leave our website and read the privacy statements of each website that may collect personal information. Equi Pulse uses WhatsApp API under the name of Equi Pulse to provide WhatsApp support to its customers.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Information Security</h2>
          <p className="text-gray-300 mb-8">
            We secure information you provide on computer servers in a controlled, secure environment, protected from unauthorized access, use, or disclosure. We keep reasonable administrative, technical, and physical safeguards to protect against unauthorized access, use, modification, and personal data disclosure in its control and custody. However, no data transmission over the Internet or wireless network can be guaranteed.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Legal Disclosure</h2>
          <p className="text-gray-300 mb-8">
            We will disclose any information we collect, use or receive if required or permitted by law, such as to comply with a subpoena or similar legal process, and when we believe in good faith that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request.
          </p>

          <h2 className="text-2xl font-bold text-amber-400 mt-8 mb-4">Contact Information</h2>
          <p className="text-gray-300 mb-4">
            If you would like to contact us to understand more about this Policy or wish to contact us concerning any matter relating to individual rights and your Personal Information, you may send an email to:
          </p>
          
          <div className="bg-slate-800/50 p-6 rounded-lg border border-amber-500/20 mt-6">
            <div className="flex flex-col gap-3">
              <a href="mailto:care@equipulseresearch.com" className="flex items-center gap-2 text-amber-400 hover:text-amber-300">
                <Mail className="w-5 h-5" aria-hidden="true" />
                care@equipulseresearch.com
              </a>
              <a href="tel:+918982892301" className="flex items-center gap-2 text-amber-400 hover:text-amber-300">
                <Phone className="w-5 h-5" aria-hidden="true" />
                +91-8982892301
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;